import type {
  BrandPalette,
  ComponentOverrides,
  ConstructorValue,
  FontFamily,
  Typography,
} from './types';
import { FONT_FALLBACK } from './types';
import { findKnob } from './schema/components';

const PALETTE_TO_GP: Partial<Record<keyof BrandPalette, string[]>> = {
  primary: ['--gp-button', '--gp-accent'],
  textOnBrand: ['--gp-button-text'],
  surfaceBorder: ['--gp-border'],
  heroFrom: ['--gp-bg-gradient-from'],
  heroTo: ['--gp-bg-gradient-to'],
  textPrimary: ['--gp-text'],
  // Page background gradient — same CSS vars as heroFrom/heroTo, but
  // exposed as a dedicated palette role so partner editors can adjust it
  // independently of the hero card. When both are supplied, this entry
  // comes later in the map and therefore wins (Object.entries preserves
  // insertion order in modern JS).
  pageBgFrom: ['--gp-bg-gradient-from'],
  pageBgTo: ['--gp-bg-gradient-to'],
};

const ALL_GP_VARS: string[] = [
  ...Object.values(PALETTE_TO_GP).flatMap((arr) => arr ?? []),
  '--gp-font-primary',
  '--gp-font-secondary',
  '--gp-font-base-size',
];

/** Knob kinds whose numeric values need a "px" suffix when written to CSS. */
const PX_NUMERIC_KINDS = new Set(['radius', 'fontSize', 'letterSpacing']);

/** Track --co-* vars written during the last applyConstructor call so we can
 *  remove them when they disappear in subsequent calls. */
let writtenCoVars: Set<string> = new Set();

/**
 * Backend-loaded ConstructorValue applied by ThemeProvider. Overlay calls
 * from the Design Constructor editor (`applyConstructorOverlay`) merge on
 * top of this — so editing one palette role doesn't wipe the other roles
 * that came from the operator's saved theme. Resets to `{ version: 2 }`
 * when no backend payload is supplied.
 */
let backendBaseline: ConstructorValue = { version: 2 };

function setOrRemove(root: HTMLElement, name: string, value: string | undefined): void {
  if (value) root.style.setProperty(name, value);
  else root.style.removeProperty(name);
}

// Matches any value that's already a valid CSS background-image keyword
// or function call. If a string matches this, the imageUrl formatter
// passes it through untouched — only bare URLs get wrapped in url("…").
const CSS_IMAGE_VALUE_RE = /^(none|inherit|initial|unset|url\(|linear-gradient\(|radial-gradient\(|conic-gradient\(|image\(|image-set\()/i;

function formatOverrideValue(
  componentName: string,
  knobName: string,
  raw: string | number,
): string {
  const knob = findKnob(componentName, knobName);
  if (typeof raw === 'string') {
    if (knob?.kind === 'imageUrl') {
      const trimmed = raw.trim();
      if (trimmed === '') return 'none';
      if (CSS_IMAGE_VALUE_RE.test(trimmed)) return trimmed;
      // Looks like a bare URL — wrap so CSS accepts it as background-image.
      // Escape any embedded double quotes (rare in URLs but cheap to guard).
      const safe = trimmed.replace(/"/g, '\\"');
      return `url("${safe}")`;
    }
    // Font-family override values may contain whitespace (e.g.
    // "Reddit Sans Condensed"); familyStack quotes them so the CSS engine
    // treats the value as ONE family, and appends a generic fallback for
    // known registry families (free-text values pass through with quoting
    // only). Mirrors the typography palette fonts.
    if (knob && knob.kind === 'fontFamily') return familyStack(raw);
    // Bare numeric STRINGS for px-suffixed kinds (e.g. "30" for a radius)
    // slip in from themes saved/loaded as JSON outside KnobControl. A
    // unitless value makes the whole CSS property invalid at computed-value
    // time (border-radius silently becomes 0), so normalize it like the
    // number path below. Values with units ("30px", "1rem") pass through.
    if (knob && PX_NUMERIC_KINDS.has(knob.kind) && raw.trim() !== '' && !Number.isNaN(Number(raw))) {
      return `${raw.trim()}px`;
    }
    return raw;
  }
  if (knob && PX_NUMERIC_KINDS.has(knob.kind)) return `${raw}px`;
  return String(raw);
}

function applyPalette(root: HTMLElement, palette: BrandPalette | undefined): void {
  const safe = palette ?? {};
  // Resolve the desired value for each CSS variable once. Later entries in
  // PALETTE_TO_GP overwrite earlier ones when their role is defined — this
  // lets `pageBgFrom` (page-level gradient) win over `heroFrom` (hero card
  // gradient) without the later mapping clobbering the earlier one with an
  // `undefined → removeProperty` when only the earlier role is supplied.
  const resolved = new Map<string, string | undefined>();
  for (const [role, vars] of Object.entries(PALETTE_TO_GP) as [keyof BrandPalette, string[]][]) {
    const colour = safe[role];
    for (const cssVar of vars) {
      if (colour) {
        resolved.set(cssVar, colour);
      } else if (!resolved.has(cssVar)) {
        // Only record the implicit "clear" if no earlier role already
        // resolved this var. Prevents pageBgFrom (undefined) from wiping
        // heroFrom's value.
        resolved.set(cssVar, undefined);
      }
    }
  }
  for (const [cssVar, value] of resolved) {
    setOrRemove(root, cssVar, value);
  }
}

/** Wrap a font-family value in quotes when it contains whitespace, so the
 *  CSS engine treats "Reddit Sans Condensed" as a single family, not three. */
function quoteFamily(name: string): string {
  return /\s/.test(name) ? `"${name}"` : name;
}

/** Full font stack: the family (quoted if multi-word) plus its generic
 *  fallback from the registry, so a blocked or slow webfont degrades to a
 *  system family instead of the browser default serif. Unknown families
 *  (e.g. a free-text override) get quoting only — no generic is appended. */
function familyStack(name: string): string {
  const generic = FONT_FALLBACK[name as FontFamily];
  return generic ? `${quoteFamily(name)}, ${generic}` : quoteFamily(name);
}

function applyTypography(root: HTMLElement, typo: Typography | undefined): void {
  setOrRemove(root, '--gp-font-primary',
    typo?.fontPrimary ? familyStack(typo.fontPrimary) : undefined);
  setOrRemove(root, '--gp-font-secondary',
    typo?.fontSecondary ? familyStack(typo.fontSecondary) : undefined);
  setOrRemove(root, '--gp-font-base-size',
    typeof typo?.baseSize === 'number' ? `${typo.baseSize}px` : undefined);
}

function applyOverrides(
  root: HTMLElement,
  overrides: ComponentOverrides | undefined,
): Set<string> {
  const next = new Set<string>();
  if (overrides) {
    for (const [componentName, knobs] of Object.entries(overrides)) {
      for (const [knobName, value] of Object.entries(knobs)) {
        const name = `--co-${componentName}-${knobName}`;
        root.style.setProperty(name, formatOverrideValue(componentName, knobName, value));
        next.add(name);
      }
    }
  }
  for (const old of writtenCoVars) {
    if (!next.has(old)) root.style.removeProperty(old);
  }
  return next;
}

/**
 * Apply ConstructorValue to :root:
 * - palette  → existing --gp-* vars
 * - overrides → --co-<Component>-<knob> vars (DS components consume via
 *   var(--co-..., fallback) from Plan 9.1 onwards)
 */
export function applyConstructor(value: ConstructorValue): void {
  const root = document.documentElement;
  applyPalette(root, value.palette);
  applyTypography(root, value.typography);
  writtenCoVars = applyOverrides(root, value.overrides);
}

/**
 * Remove every variable this module writes. Idempotent. Resets internal
 * --co-* tracking and the backend baseline too.
 */
export function clearConstructor(): void {
  const root = document.documentElement;
  for (const cssVar of ALL_GP_VARS) root.style.removeProperty(cssVar);
  for (const cssVar of writtenCoVars) root.style.removeProperty(cssVar);
  writtenCoVars = new Set();
  backendBaseline = { version: 2 };
}

/**
 * Set the backend baseline AND apply it. Called by ThemeProvider once the
 * operator's design_system payload has been loaded. Stores the full v2
 * value so subsequent overlay applies (from the Design Constructor editor)
 * can merge against the saved theme instead of replacing it.
 */
export function setConstructorBaseline(value: ConstructorValue): void {
  backendBaseline = value;
  applyConstructor(value);
  // When this iframe is loaded as the Design Constructor preview child,
  // broadcast the freshly-loaded baseline to the parent so its Copy JSON
  // includes the partner's full saved theme (not just the operator's
  // current-session tweaks). Same `design-constructor-baseline` message
  // type is consumed by `useBaselineSync` in the parent.
  if (typeof window !== 'undefined' && window.parent !== window) {
    const flag = new URLSearchParams(window.location.search).get('design_constructor');
    if (flag === 'child') {
      window.parent.postMessage(
        { type: 'design-constructor-baseline', value },
        '*',
      );
    }
  }
}

/** Read-only view of the backend baseline the iframe last received from
 *  `setConstructorBaseline`. Returns `{ version: 2 }` when nothing has
 *  been set yet (e.g. before ThemeProvider's fetch completes). */
export function getConstructorBaseline(): ConstructorValue {
  return backendBaseline;
}

function mergeConstructorValues(
  base: ConstructorValue,
  top: ConstructorValue,
): ConstructorValue {
  const result: ConstructorValue = { version: 2 };

  const palette: BrandPalette = { ...(base.palette ?? {}), ...(top.palette ?? {}) };
  if (Object.keys(palette).length > 0) result.palette = palette;

  const typography: Typography = {
    ...(base.typography ?? {}),
    ...(top.typography ?? {}),
  };
  if (Object.keys(typography).length > 0) result.typography = typography;

  const overrides: ComponentOverrides = {};
  if (base.overrides) {
    for (const [component, knobs] of Object.entries(base.overrides)) {
      overrides[component] = { ...knobs };
    }
  }
  if (top.overrides) {
    for (const [component, knobs] of Object.entries(top.overrides)) {
      overrides[component] = { ...(overrides[component] ?? {}), ...knobs };
    }
  }
  if (Object.keys(overrides).length > 0) result.overrides = overrides;

  return result;
}

/**
 * Apply an overlay ConstructorValue from the Design Constructor editor on
 * top of the current backend baseline. Missing keys in `overlay` fall
 * through to the baseline — so editing one palette role keeps the rest of
 * the operator's saved theme intact. Calls applyConstructor with the
 * merged result.
 */
export function applyConstructorOverlay(overlay: ConstructorValue): void {
  applyConstructor(mergeConstructorValues(backendBaseline, overlay));
}

/**
 * Return the full effective theme = backend baseline merged with the
 * editor overlay. Used by Copy JSON so the operator always exports the
 * complete payload (existing partner theme + their fresh tweaks), not
 * just the delta they touched in this session.
 */
export function getEffectiveConstructorValue(overlay: ConstructorValue): ConstructorValue {
  return mergeConstructorValues(backendBaseline, overlay);
}
